import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Home.css';

const Home: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>Professional Wildlife Cull Management for England</h1>
          <p className="hero-subtitle">
            Track, manage, and analyze your wildlife culling records with our comprehensive digital platform. 
            Built for professional wildlife managers across England - supporting deer, fox, rabbit, and more.
          </p>
          <div className="hero-buttons">
            {!isAuthenticated ? (
              <Link to="/login" className="cta-button primary">
                Get Started Free
              </Link>
            ) : (
              <Link to="/dashboard" className="cta-button primary">
                Go to Dashboard
              </Link>
            )}
            <a href="#features" className="cta-button secondary">
              Learn More
            </a>
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-placeholder">
            🦌 🗺️ 📊
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <h2>Everything You Need for Professional Wildlife Management</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🗺️</div>
              <h3>Interactive Mapping</h3>
              <p>Visualize all your cull locations on detailed maps of England. Track patterns and manage territories for deer, fox, rabbit, and other species.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📝</div>
              <h3>Detailed Record Keeping</h3>
              <p>Record comprehensive details including species, weight, condition, shot placement, and GPS coordinates.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Analytics & Reports</h3>
              <p>Generate detailed reports and analytics to track your management effectiveness over time.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📱</div>
              <h3>Mobile Friendly</h3>
              <p>Access your data anywhere, anytime. Perfect for field use on tablets and smartphones.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Secure & Private</h3>
              <p>Your data is securely stored and only accessible to you. Built with privacy and security in mind.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📈</div>
              <h3>Export Data</h3>
              <p>Export your records to CSV for analysis, reporting, or integration with other systems.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <h2>Why Choose culr.app?</h2>
            <div className="benefits-list">
              <div className="benefit-item">
                <span className="benefit-number">1</span>
                <div className="benefit-text">
                  <h3>Save Time</h3>
                  <p>Digital record keeping eliminates paperwork and reduces administrative overhead.</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-number">2</span>
                <div className="benefit-text">
                  <h3>Improve Accuracy</h3>
                  <p>GPS coordinates and standardized forms ensure consistent, accurate data collection.</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-number">3</span>
                <div className="benefit-text">
                  <h3>Make Better Decisions</h3>
                  <p>Data-driven insights help optimize your deer management strategy.</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="benefit-number">4</span>
                <div className="benefit-text">
                  <h3>Stay Compliant</h3>
                  <p>Maintain detailed records for regulatory compliance and best practice documentation.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Modernize Your Wildlife Management?</h2>
          <p>Join professional wildlife managers across England who trust culr.app for their record keeping.</p>
          {!isAuthenticated ? (
            <Link to="/login" className="cta-button primary large">
              Start Tracking Today
            </Link>
          ) : (
            <Link to="/dashboard" className="cta-button primary large">
              Go to Dashboard
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
